// The trait table. A trait is a condition or a timer on a piece: stopped,
// a captor killer, a timed blast. Every surface that shows or ticks a trait reads this
// table, so a new trait is one row here plus the rule that reads it in
// moves.ts or apply.ts. The badges, the inspector, the board redraw, the
// turn tick, and the strip of gains all iterate TRAITS and never name a field.

import type { Board, Piece, Side } from './types';

/** Fields that count down at the start of the owner's turn. */
export type TimerKey =
  | 'stoppedTurns'
  | 'uncapturableTurns'
  | 'knightTurns'
  | 'passThroughTurns'
  | 'backwardTurns'
  | 'randomMoveTurns'
  | 'explodeIn'
  | 'dormantTurns'
  | 'cloneIn';

/** Fields that are on or off. heldBy holds the side that holds it. */
export type FlagKey =
  | 'randomNextMove'
  | 'shortSlides'
  | 'movesTwice'
  | 'capturesAgain'
  | 'killsCaptor'
  | 'demotesCaptor'
  | 'stealsAbility'
  | 'passesAbilities'
  | 'mustCapture'
  | 'pushes'
  | 'heldBy';

export type TraitKey = TimerKey | FlagKey;

/**
 * Which corner of the square the badge sits in. Capture traits fire when the
 * piece is taken. Conditions change how it moves. Crown holds timers that end
 * the piece. Null means another surface draws it.
 */
export type TraitCorner = 'capture' | 'condition' | 'royalty' | null;

export type TraitSymbol =
  | { style: 'mark'; d: string }
  | { style: 'line'; d: string }
  | { style: 'text'; value: string }
  | { style: 'count' };

export type Trait = {
  key: TraitKey;
  /** One line for the inspector. */
  describe: (piece: Piece) => string;
  corner: TraitCorner;
  /** Picks the badge color. */
  tone: string;
  symbol: TraitSymbol;
  /** Counts down at the start of the owner's turn. */
  timed: boolean;
  /** The log line when the timer runs out. Timers that fire instead have none. */
  expiry: string | null;
  /** A buff gave it. A strip of gains clears it. Debuffs stay. */
  fromBuff: boolean;
  /** A timed badge shows the turns left. */
  showCount: boolean;
  /** The badge is a bomb: a fuse with a spark comes off the circle. */
  fuse?: boolean;
  /** The badge blinks at this count: the timer fires at the start of the next turn of the owner. */
  blinkAtCount?: number;
};

const HEART = 'M0 4 C-6 -2 -6 -9 -1 -8 C1 -8 1 -6 0 -5 C-1 -6 -1 -8 1 -8 C6 -9 6 -2 0 4 Z';
const UNCAPTURABLE = 'M0 -7 L7 -4 C7 2 4 6 0 8 C-4 6 -7 2 -7 -4 Z';
const BOLT = 'M2 -8 L-5 1 L-1 1 L-2 8 L5 -1 L1 -1 Z';
const SNOW = 'M0 -8 L0 8 M-7 -4 L7 4 M-7 4 L7 -4';
const LEGS = 'M-4 -2 L-4 6 M4 -2 L4 6 M-6 -4 L6 -4';
const SKULL =
  'M-6 -2 A6 6 0 1 1 6 -2 L6 4 L-6 4 Z M-3 -2 A1.5 1.5 0 1 0 -3 1 Z M3 -2 A1.5 1.5 0 1 0 3 1 Z';
const FANGS = 'M-6 -4 L6 -4 L4 0 L2 6 L0 0 L-2 6 L-4 0 Z';
const HALO = 'M0 -7 A7 7 0 1 1 0 7 A7 7 0 1 1 0 -7 Z M0 -3 A3 3 0 1 0 0 3 A3 3 0 1 0 0 -3 Z';
const SCROLL = 'M-6 -6 L6 -6 L6 6 L-6 6 Z M-3 -2 L3 -2 M-3 2 L3 2';
const MOUTH = 'M-7 0 A7 7 0 0 0 7 0 Z M-5 -4 L5 -4';
const PASS_THROUGH = 'M-6 6 L-6 -2 A6 6 0 0 1 6 -2 L6 6 L3 3 L0 6 L-3 3 Z';
const BACK = 'M5 -6 L-3 0 L5 6 Z M-5 -6 L-5 6';
const SLEEP = 'M-6 -6 L2 -6 L-6 2 L2 2 M3 2 L8 2 L3 7 L8 7';
const PUSH = 'M-8 -6 L-5 -6 L-5 6 L-8 6 Z M-3 -2 L2 -2 L2 -6 L8 0 L2 6 L2 2 L-3 2 Z';
const AGAIN = 'M-7 -6 L-1 0 L-7 6 M1 -6 L7 0 L1 6';
const ROPE = 'M-7 -7 L7 7 M-7 7 L7 -7 M0 -7 L0 7';

export const HEART_PATH = HEART;

const plural = (count: number, word: string): string => `${count} ${word}${count === 1 ? '' : 's'}`;

const turnsLeft = (piece: Piece, key: TimerKey, what: string): string =>
  `${what} for ${plural(piece[key], 'more turn')}`;

export const TRAITS: readonly Trait[] = [
  // ---- Capture hooks. They fire when the piece is taken. ----
  {
    key: 'killsCaptor',
    describe: () => 'whoever takes it is removed too',
    corner: 'capture',
    tone: 'kills-captor',
    symbol: { style: 'mark', d: SKULL },
    timed: false,
    expiry: null,
    fromBuff: true,
    showCount: false,
  },
  {
    key: 'demotesCaptor',
    describe: () => 'whoever takes it becomes a pawn',
    corner: 'capture',
    tone: 'demotes-captor',
    symbol: { style: 'mark', d: HALO },
    timed: false,
    expiry: null,
    fromBuff: true,
    showCount: false,
  },
  {
    key: 'stealsAbility',
    describe: () => 'takes one ability from each piece it captures',
    corner: 'capture',
    tone: 'steals',
    symbol: { style: 'mark', d: FANGS },
    timed: false,
    expiry: null,
    fromBuff: true,
    showCount: false,
  },
  {
    key: 'passesAbilities',
    describe: () => 'a neighbor gets its abilities when it is taken',
    corner: 'capture',
    tone: 'passes',
    symbol: { style: 'line', d: SCROLL },
    timed: false,
    expiry: null,
    fromBuff: true,
    showCount: false,
  },

  // ---- Conditions. They change how the piece moves. ----
  {
    key: 'stoppedTurns',
    describe: (piece) => turnsLeft(piece, 'stoppedTurns', 'stopped'),
    corner: 'condition',
    tone: 'stopped',
    symbol: { style: 'line', d: SNOW },
    timed: true,
    expiry: 'can move again',
    fromBuff: false,
    showCount: true,
  },
  {
    key: 'heldBy',
    describe: (piece) => `held until ${piece.heldBy} loses a piece`,
    corner: 'condition',
    tone: 'held',
    symbol: { style: 'line', d: ROPE },
    timed: false,
    expiry: null,
    fromBuff: false,
    showCount: false,
  },
  {
    key: 'uncapturableTurns',
    describe: (piece) => turnsLeft(piece, 'uncapturableTurns', 'cannot be captured'),
    corner: 'condition',
    tone: 'uncapturable',
    symbol: { style: 'mark', d: UNCAPTURABLE },
    timed: true,
    expiry: 'can be captured again',
    fromBuff: true,
    showCount: true,
  },
  {
    key: 'movesTwice',
    describe: () => 'moves twice this turn',
    corner: 'condition',
    tone: 'twice',
    symbol: { style: 'mark', d: BOLT },
    timed: false,
    expiry: null,
    fromBuff: true,
    showCount: false,
  },
  {
    key: 'capturesAgain',
    describe: () => 'moves again the next time it captures',
    corner: 'condition',
    tone: 'twice',
    symbol: { style: 'line', d: AGAIN },
    timed: false,
    expiry: null,
    fromBuff: true,
    showCount: false,
  },
  {
    key: 'cloneIn',
    describe: (piece) =>
      `a copy of it comes in ${plural(piece.cloneIn, 'turn')}, on an empty square beside it`,
    corner: 'condition',
    tone: 'clone',
    symbol: { style: 'count' },
    timed: true,
    expiry: null,
    fromBuff: true,
    showCount: false,
  },
  {
    key: 'pushes',
    describe: () => 'pushes a piece it moves into to a free square beside that piece',
    corner: 'condition',
    tone: 'pushes',
    symbol: { style: 'mark', d: PUSH },
    timed: false,
    expiry: null,
    fromBuff: true,
    showCount: false,
  },
  {
    key: 'randomNextMove',
    describe: () => 'its next move goes somewhere random',
    corner: 'condition',
    tone: 'random-next',
    symbol: { style: 'text', value: '?' },
    timed: false,
    expiry: null,
    fromBuff: false,
    showCount: false,
  },
  {
    key: 'shortSlides',
    describe: () => 'slides one square only',
    corner: 'condition',
    tone: 'short-slides',
    symbol: { style: 'line', d: LEGS },
    timed: false,
    expiry: null,
    fromBuff: false,
    showCount: false,
  },
  {
    key: 'passThroughTurns',
    describe: (piece) => turnsLeft(piece, 'passThroughTurns', 'slides through pieces'),
    corner: 'condition',
    tone: 'pass-through',
    symbol: { style: 'mark', d: PASS_THROUGH },
    timed: true,
    expiry: 'is blocked by pieces again',
    fromBuff: true,
    showCount: true,
  },
  {
    key: 'backwardTurns',
    describe: (piece) => turnsLeft(piece, 'backwardTurns', 'can only move backward'),
    corner: 'condition',
    tone: 'backward',
    symbol: { style: 'line', d: BACK },
    timed: true,
    expiry: 'can advance again',
    fromBuff: false,
    showCount: true,
  },
  {
    key: 'randomMoveTurns',
    describe: (piece) => turnsLeft(piece, 'randomMoveTurns', 'sleepwalks before each turn'),
    corner: 'condition',
    tone: 'random-move',
    symbol: { style: 'line', d: SLEEP },
    timed: true,
    expiry: 'moves on its own no more',
    fromBuff: false,
    showCount: true,
  },
  {
    key: 'mustCapture',
    describe: () => 'must capture when it can',
    corner: 'condition',
    tone: 'must-capture',
    symbol: { style: 'mark', d: MOUTH },
    timed: false,
    expiry: null,
    fromBuff: false,
    showCount: false,
  },

  // ---- Timers that end the piece. ----
  {
    key: 'explodeIn',
    describe: (piece) => `explodes in ${plural(piece.explodeIn, 'turn')}, with its neighbors`,
    corner: 'royalty',
    tone: 'explode',
    symbol: { style: 'count' },
    timed: true,
    expiry: null,
    fromBuff: false,
    showCount: false,
    fuse: true,
    blinkAtCount: 1,
  },
  {
    key: 'dormantTurns',
    describe: (piece) => `an dormant: wakes in ${plural(piece.dormantTurns, 'turn')}`,
    corner: 'royalty',
    tone: 'dormant',
    symbol: { style: 'count' },
    timed: true,
    expiry: null,
    fromBuff: false,
    showCount: false,
  },

  // ---- Drawn elsewhere. The learned abilities row shows the jump. ----
  {
    key: 'knightTurns',
    describe: (piece) => turnsLeft(piece, 'knightTurns', 'jumps like a knight'),
    corner: null,
    tone: 'temporary',
    symbol: { style: 'count' },
    timed: true,
    expiry: 'lost its knight moves',
    fromBuff: true,
    showCount: false,
  },
];

export const TIMED_TRAITS: readonly Trait[] = TRAITS.filter((trait) => trait.timed);
export const BUFF_TRAITS: readonly Trait[] = TRAITS.filter((trait) => trait.fromBuff);

export const isTimer = (key: TraitKey): key is TimerKey =>
  TIMED_TRAITS.some((trait) => trait.key === key);

/** True while the trait is on the piece. */
export const hasTrait = (piece: Piece, trait: Trait): boolean => {
  const value = piece[trait.key];
  if (typeof value === 'number') return value > 0;
  return value !== false && value !== null;
};

/** The turns left on a timer, or zero for a flag. */
export const traitCount = (piece: Piece, trait: Trait): number => {
  const value = piece[trait.key];
  return typeof value === 'number' ? value : 0;
};

/** A string that changes when any trait changes. The board redraw reads it. */
export const traitSignature = (piece: Piece): string =>
  TRAITS.map((trait) => String(piece[trait.key])).join(',');

/** The piece with every buff trait removed. */
export const withoutBuffTraits = (piece: Piece): Piece => {
  const next = { ...piece };
  for (const trait of BUFF_TRAITS) {
    if (isTimer(trait.key)) next[trait.key] = 0;
    else if (trait.key === 'heldBy') next.heldBy = null;
    else next[trait.key] = false;
  }
  return next;
};

export const hasAnyBuffTrait = (piece: Piece): boolean =>
  BUFF_TRAITS.some((trait) => hasTrait(piece, trait));

/** Every timer one lower, floored at zero. */
export const tickTimers = (piece: Piece): Piece => {
  const next = { ...piece };
  for (const trait of TIMED_TRAITS) {
    const key = trait.key as TimerKey;
    next[key] = Math.max(0, piece[key] - 1);
  }
  return next;
};

const hasRunningTimer = (piece: Piece): boolean =>
  TIMED_TRAITS.some((trait) => piece[trait.key as TimerKey] > 0);

/**
 * The timers of the pieces of the side one turn lower, as at the start of its
 * turn. The search calls it at each turn in its tree, so a stopped piece can
 * move again there when it can in the game. The board is the same object when
 * no timer runs.
 */
export const tickSideTimers = (board: Board, side: Side): Board =>
  board.some((piece) => piece?.owner === side && hasRunningTimer(piece))
    ? board.map((piece) =>
        piece?.owner === side && hasRunningTimer(piece) ? tickTimers(piece) : piece,
      )
    : board;

/** Traits whose timer is about to run out, with the line to log. */
export const expiringTraits = (piece: Piece): Trait[] =>
  TIMED_TRAITS.filter((trait) => trait.expiry !== null && traitCount(piece, trait) === 1);

export const traitsInCorner = (piece: Piece, corner: TraitCorner): Trait[] =>
  TRAITS.filter((trait) => trait.corner === corner && hasTrait(piece, trait));

export const activeTraits = (piece: Piece): Trait[] =>
  TRAITS.filter((trait) => hasTrait(piece, trait));
